/**
 * IOSScreen — full-height scroll wrapper with optional sticky top blur edge.
 *
 * Handles scroll-tracking for the top glass edge opacity fade.
 * Optional tab-bar bottom padding via tabBarSpace prop (FTR-998 — uses TAB_BAR.clearance + safe-area inset).
 * Atom purity: imports only react + tokens.
 */
import { CSSProperties, ReactNode, Ref, useRef, useState } from 'react';
import { P, TAB_BAR } from '../../styles/tokens';

export interface IOSScreenProps {
  children: ReactNode;
  /** Dark-surface mode: white text, dark edge strip. */
  dark?: boolean;
  /** Override the edge strip background. */
  edgeBg?: string;
  /** External scroll ref (passed in by screen for external scroll-tracking). */
  scrollRef?: Ref<HTMLDivElement>;
  /** Add bottom padding to clear the floating tab bar (FTR-998). Defaults to false — backward-compatible. */
  tabBarSpace?: boolean;
}

export function IOSScreen({ children, dark = false, edgeBg, scrollRef: extRef, tabBarSpace = false }: IOSScreenProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const scrollRef = (extRef ?? innerRef) as React.RefObject<HTMLDivElement>;

  const [scrolled, setScrolled] = useState(0); // 0..1

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const top = (e.target as HTMLDivElement).scrollTop;
    setScrolled(Math.max(0, Math.min(1, top / 24)));
  };

  const stripBg: string =
    edgeBg !== undefined
      ? edgeBg
      : dark
        ? 'rgba(20,16,10,0.6)'
        : 'rgba(242,239,230,0.6)';

  const wrapperStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    background: dark ? P.ink : P.cream,
  };

  const scrollStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingTop: 54,
    paddingBottom: tabBarSpace
      ? `calc(${TAB_BAR.clearance}px + var(--tg-safe-area-inset-bottom, 0px))`
      : undefined,
    WebkitOverflowScrolling: 'touch' as CSSProperties['WebkitOverflowScrolling'],
    boxSizing: 'border-box',
  };

  const edgeStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 54,
    pointerEvents: 'none',
    zIndex: 9,
    opacity: scrolled,
    transition: 'opacity 0.18s ease',
  };

  const blurStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    backdropFilter: 'blur(18px) saturate(180%)',
    WebkitBackdropFilter: 'blur(18px) saturate(180%)',
    background: stripBg,
    maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
    WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
  };

  const borderStyle: CSSProperties = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 0.5,
    background: dark ? 'rgba(255,255,255,0.10)' : 'rgba(60,50,35,0.10)',
  };

  return (
    <div style={wrapperStyle}>
      <div ref={scrollRef} onScroll={handleScroll} style={scrollStyle}>
        {children}
      </div>
      {/* Scroll-edge blur strip — fades in as content scrolls under */}
      <div style={edgeStyle}>
        <div style={blurStyle} />
        <div style={borderStyle} />
      </div>
    </div>
  );
}
